/**
 * Edge-health board overlay — draws a colored stroke over each modeled edge that
 * needs attention: red for erroring edges, a dim dashed ghost for modeled edges
 * with no live traffic (unobserved). Healthy confirmed edges get nothing (the
 * particle flow already shows them). Renders inside the space edge SVG, so it
 * inherits the exact edge geometry + board transform, exactly like the particle
 * layer. A board-layer plugin CONTRIBUTION — the board never mounts it.
 *
 * No-ops when no telemetry is flowing (empty snapshot), so a board without a
 * live feed isn't painted all-dashed.
 */
import { memo } from "react";
import type { BoardEdge, PlannedRoute } from "@archlang/scene/board";
import { useLiveEdgeStats } from "../liveStatsStore.js";
import { trafficEdgeKey } from "../liveTrafficStore.js";
import { edgeServicePair } from "../edgeServicePair.js";
import { edgeHealthStyle } from "./edgeStyle.js";

export interface TelemetryEdgeLayerProps {
  readonly edges: readonly BoardEdge[];
  readonly routes: ReadonlyMap<string, PlannedRoute>;
}

function TelemetryEdgeLayerImpl({ edges, routes }: TelemetryEdgeLayerProps) {
  const stats = useLiveEdgeStats();
  // Telemetry off / idle → draw nothing (don't ghost every edge).
  if (stats.size === 0) return <g className="telemetry-edge-layer" />;

  return (
    <g className="telemetry-edge-layer" style={{ pointerEvents: "none" }}>
      {edges.map((e) => {
        const pair = edgeServicePair(e);
        if (!pair) return null;
        const route = routes.get(e.id);
        if (!route) return null;
        const style = edgeHealthStyle(stats.get(trafficEdgeKey(pair[0], pair[1])));
        if (!style) return null;
        return (
          <path
            key={e.id}
            d={route.d}
            fill="none"
            stroke={style.stroke}
            strokeWidth={style.width}
            strokeLinecap="round"
            {...(style.dash ? { strokeDasharray: style.dash } : {})}
            opacity={style.opacity}
            data-edge-health={style.kind}
          />
        );
      })}
    </g>
  );
}

export const TelemetryEdgeLayer = memo(TelemetryEdgeLayerImpl);
